// ============================================
// RANKS MANAGEMENT PAGE
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Medal,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  StatusBadge,
  ConfirmDialog,
  RankModal,
} from '@/components';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/ranks/')({
  component: RanksPage,
});

// Rank tipi (backend schema ile uyumlu)
interface Rank {
  id: string;
  code: string;
  nameEn: string;
  nameTr: string;
  department: string;
  level: number;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const departmentLabels: Record<string, string> = {
  DECK: 'Güverte',
  ENGINE: 'Makine',
  ELECTRICAL: 'Elektrik',
  CATERING: 'Catering',
  OTHER: 'Diğer',
};

function RanksPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [modalState, setModalState] = useState<{
    open: boolean;
    editData: Rank | null;
  }>({ open: false, editData: null });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    rank: Rank | null;
  }>({ open: false, rank: null });

  // Rütbeleri getir
  const { data, isLoading } = useQuery<Rank[]>({
    queryKey: ['admin', 'ranks'],
    queryFn: () => adminApi.getRanks(),
  });

  // Silme mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteRank(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ranks'] });
      setDeleteDialog({ open: false, rank: null });
    },
    onError: (error: any) => {
      // Bağımlılık hatası durumunda alert göster
      const message = error?.response?.data?.message || 'Silme işlemi başarısız';
      alert(message);
      setDeleteDialog({ open: false, rank: null });
    },
  });

  const columns = [
    {
      key: 'rank',
      header: 'Rütbe',
      render: (rank: Rank) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-maritime bg-indigo-100 text-indigo-700">
            <Medal className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{rank.nameTr}</p>
            <p className="text-xs text-gray-500">{rank.nameEn} — {rank.code}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Departman',
      render: (rank: Rank) => (
        <span
          className={cn(
            'px-2.5 py-0.5 text-xs font-medium rounded-full',
            rank.department === 'DECK' && 'bg-blue-100 text-blue-800',
            rank.department === 'ENGINE' && 'bg-green-100 text-green-800',
            rank.department === 'ELECTRICAL' && 'bg-amber-100 text-amber-800',
            rank.department === 'CATERING' && 'bg-purple-100 text-purple-800',
            rank.department === 'OTHER' && 'bg-gray-100 text-gray-800',
          )}
        >
          {departmentLabels[rank.department] || rank.department}
        </span>
      ),
    },
    {
      key: 'level',
      header: 'Seviye',
      render: (rank: Rank) => (
        <span className="text-gray-600 font-medium">{rank.level}</span>
      ),
    },
    {
      key: 'sortOrder',
      header: 'Sıralama',
      render: (rank: Rank) => (
        <span className="text-gray-500">{rank.sortOrder}</span>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      render: (rank: Rank) => (
        <span
          className={cn(
            'inline-flex items-center gap-1 text-sm',
            rank.isActive ? 'text-green-600' : 'text-gray-400'
          )}
        >
          {rank.isActive ? (
            <>
              <CheckCircle className="h-4 w-4" />
              Aktif
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4" />
              Pasif
            </>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (rank: Rank) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <button
                onClick={() => setModalState({ open: true, editData: rank })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
              >
                <Edit className="h-4 w-4" />
                Düzenle
              </button>
              <hr className="my-1" />
              <button
                onClick={() => setDeleteDialog({ open: true, rank })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
              >
                <Trash2 className="h-4 w-4" />
                Sil
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const departmentOptions = [
    { value: 'DECK', label: 'Güverte' },
    { value: 'ENGINE', label: 'Makine' },
    { value: 'ELECTRICAL', label: 'Elektrik' },
    { value: 'CATERING', label: 'Catering' },
    { value: 'OTHER', label: 'Diğer' },
  ];

  // Veri filtreleme
  const ranksArray = Array.isArray(data) ? data : ((data as any)?.data || []);
  const filteredData = ranksArray.filter((rank: Rank) => {
    const matchesSearch =
      !search ||
      rank.nameTr?.toLowerCase().includes(search.toLowerCase()) ||
      rank.nameEn?.toLowerCase().includes(search.toLowerCase()) ||
      rank.code?.toLowerCase().includes(search.toLowerCase());
    const matchesDepartment = !departmentFilter || rank.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  const activeFiltersCount =
    (departmentFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rütbeler"
        description="Sistem rütbelerini yönetin"
        actions={
          <button
            onClick={() => setModalState({ open: true, editData: null })}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Yeni Rütbe
          </button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Rütbe adı veya kodu ile ara..."
        filters={
          <FilterSelect
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={departmentOptions}
            placeholder="Tüm Departmanlar"
          />
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setDepartmentFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={filteredData}
        keyExtractor={(rank) => rank.id}
        loading={isLoading}
        emptyMessage="Rütbe bulunamadı"
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
        title="Rütbeyi Sil"
        description={`"${deleteDialog.rank?.nameTr}" rütbesini silmek istediğinize emin misiniz? Bağlı denizci, ilan veya hizmet kaydı varsa silme işlemi reddedilecektir.`}
        variant="danger"
        confirmText="Sil"
        onConfirm={() => {
          if (deleteDialog.rank) {
            deleteMutation.mutate(deleteDialog.rank.id);
          }
        }}
        loading={deleteMutation.isPending}
      />

      <RankModal
        open={modalState.open}
        onClose={() => setModalState({ open: false, editData: null })}
        editData={modalState.editData}
      />
    </div>
  );
}
